import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/shared/store/authStore'
import { isRefreshRateLimited, refreshSession } from '@/api/client'
import { queryClient } from '@/shared/lib/queryClientSingleton'
import { preferencesQuery } from '@/api/preferencesQuery'
import { Button } from '@/shared/ui/button'
import { rememberSessionEndNotice } from '@/features/auth/sessionEndNotice'

/**
 * sessionStorage key holding the in-app path a member was trying to reach before
 * being bounced to sign in (e.g. following a `/booking-requests` deep link from a
 * studio notification email on a browser without a live refresh cookie). Read +
 * cleared by the login flow so the member lands where they intended instead of the
 * dashboard — the main re-login-friction reducer for email/PWA deep links.
 */
export const POST_LOGIN_REDIRECT_KEY = 'sf_post_login_redirect'

/** `/` — the one studio-tree URL a first-time visitor reaches without a session. */
const isBareDomain = (pathname: string) => pathname === '/'

/** Only same-origin app paths are safe redirect targets (no open redirect, no auth loop). */
export function isSafeRedirectPath(path: string | null | undefined): path is string {
  return !!path && path.startsWith('/') && !path.startsWith('//')
    && !path.startsWith('/welcome') && !path.startsWith('/login')
}

/**
 * Boot-time auth resolver. Access tokens live in memory only (the
 * accompanying refresh token is in an HttpOnly cookie — see
 * `frontend/src/api/client.ts`), so a hard reload always starts with no
 * access token. We optimistically POST `/auth/refresh`: if the browser has
 * a valid refresh cookie the call succeeds and we hydrate the auth store;
 * otherwise the server returns 400, we fall through to the welcome page,
 * and the user signs in again.
 *
 * The previous implementation gated the refresh attempt on a
 * `localStorage` flag (the now-removed `refreshStore`), which leaked the
 * refresh token to JS. Doing an unconditional refresh on mount is one
 * extra round-trip on a cold load — small price for not having a
 * 7-day-lived JWT inside reach of any XSS payload.
 */
export function ProtectedRoute() {
  const { t } = useTranslation()
  const { accessToken, setAuth, clearAuth, teamMember } = useAuthStore()
  const location = useLocation()
  const [checking, setChecking] = useState(!accessToken)
  const [refreshFailed, setRefreshFailed] = useState(false)
  // The boot refresh was refused for volume (429) even after its retry: the cookie may be
  // perfectly good, so this is a "try again" screen, never the welcome page (RB-28 item 3).
  const [refreshBusy, setRefreshBusy] = useState(false)

  const bootRefresh = () => {
    // Start the dashboard chunk NOW, alongside the refresh rather than after it.
    // Nothing about a static chunk needs a token, and the old order was strictly
    // serial: refresh → HomeRedirect mounts → preferences → only then does the
    // route chunk begin downloading. This is free parallelism; if the member's
    // home page turns out to be something else, the cost is one warm chunk.
    void import('@/features/dashboard/pages/DashboardPage')

    // refreshSession(), not a bare POST: this effect is not the only thing that can
    // ask for a refresh on a cold load — a 401 from anything that fires alongside it
    // enters the axios interceptor, which would otherwise present the SAME rotating
    // cookie a second time and fork the session chain (see api/client.ts). StrictMode
    // double-invoking this effect in dev did it all by itself. Sharing one in-flight
    // promise makes every extra caller join instead of racing.
    refreshSession()
      .then((data) => {
        setAuth(data.accessToken, data.member ?? teamMember!)
        // Warm the preferences the instant we have a token, instead of waiting for
        // HomeRedirect to mount and ask. Same query definition, so HomeRedirect's
        // useQuery joins this in-flight request rather than issuing a second one.
        //
        // NOTE, deliberately not the runbook's version. The runbook says to fire
        // this "in parallel with the refresh", relying on the 401 interceptor to
        // retry once the store hydrates. That is not free: the request goes out
        // unauthenticated, comes back 401, and the interceptor answers it with
        // ANOTHER /auth/refresh — trading the round trip we are removing for a
        // different one, plus a wasted request. Firing it here costs nothing and
        // removes the mount hop, which is the part that was actually serial.
        void queryClient.prefetchQuery(preferencesQuery)
      })
      .catch((err) => {
        // Refused for volume, not as a session: say so and offer a retry; touch nothing.
        if (isRefreshRateLimited(err)) {
          setRefreshBusy(true)
          return
        }
        // Either no cookie (fresh visitor) or it's expired/revoked. Either
        // way the user has to sign in again — but remember where they were
        // heading so login can return them there (e.g. an email deep link to
        // /booking-requests) instead of dumping them on the dashboard.
        clearAuth()
        const intended = location.pathname + location.search
        if (isSafeRedirectPath(intended)) {
          sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, intended)
        }
        // A studio page deserves the sign-in form and an explanation (D62); only the bare
        // domain is a place a stranger lands, and stays on the welcome page.
        if (!isBareDomain(location.pathname)) rememberSessionEndNotice({ reason: 'EXPIRED' })
        setRefreshFailed(true)
      })
      .finally(() => setChecking(false))
  }

  useEffect(() => {
    if (!checking) return
    bootRefresh()
  }, [])

  if (checking) {
    return (
      <div className="flex h-screen items-center justify-center">
        <span className="text-muted-foreground">{t('common:common.loading')}</span>
      </div>
    )
  }

  if (refreshBusy) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 px-6 text-center" role="alert">
        <p className="font-medium">{t('common:common.refreshBusy.title')}</p>
        <p className="max-w-sm text-sm text-muted-foreground">{t('common:common.refreshBusy.message')}</p>
        <Button
          type="button"
          variant="outline"
          onClick={() => { setRefreshBusy(false); setChecking(true); bootRefresh() }}
        >
          {t('common:common.refreshBusy.retry')}
        </Button>
      </div>
    )
  }

  // Two different "no token" situations, two destinations (D33):
  //  - the boot refresh FAILED: nobody has had a session in this tab. On the bare
  //    domain that is a visitor — the public welcome page. On any studio page it is a
  //    member whose session ended (a bookmark, an email link, "sign out everywhere"):
  //    the sign-in form, with the return URL kept and a line saying why (F42 / D62).
  //  - we HAD a token and it is gone: a session ended here — Sign out, Sign out
  //    everywhere, account deletion. Every one of those handlers runs `clearAuth()`
  //    and then `navigate('/login')`; the store write re-renders this route before
  //    the router moves, and when this branch answered "/welcome" too the two
  //    navigations raced and Logout landed on either page. Pointing it at /login
  //    makes both agree, whichever commits last. The axios interceptor's
  //    dead-session path already hard-navigates to /login for the same reason.
  if (refreshFailed) {
    return <Navigate to={isBareDomain(location.pathname) ? '/welcome' : '/login'} replace />
  }
  if (!accessToken) {
    return <Navigate to="/login" replace />
  }

  if (teamMember && !teamMember.termsAcceptedAt) {
    // Carry where the member was going (A-F4): the paid-plan path signs in here after Stripe,
    // and a bare /accept-terms dropped every destination, so /onboarding was never offered.
    const here = location.pathname + location.search
    const to = here === '/' || here.startsWith('/accept-terms')
      ? '/accept-terms'
      : `/accept-terms?next=${encodeURIComponent(here)}`
    return <Navigate to={to} replace />
  }

  return <Outlet />
}
